import { CaretUpDown, Check, CaretRight } from "@phosphor-icons/react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@reactive-resume/utils";
import { forwardRef, useState, useEffect } from "react";

export interface CascaderOption {
  value: string;
  label: string;
  children?: CascaderOption[];
}

export interface CascaderProps {
  options: CascaderOption[];
  value?: string[];
  onChange?: (value: string[], selectedOptions: CascaderOption[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export const Cascader = forwardRef<HTMLButtonElement, CascaderProps>(
  ({ options, value = [], onChange, placeholder = "请选择", disabled = false, className }, ref) => {
    const [open, setOpen] = useState(false);
    const [selectedPath, setSelectedPath] = useState<string[]>(value);
    const [currentLevel, setCurrentLevel] = useState(0);
    const [levelOptions, setLevelOptions] = useState<CascaderOption[][]>([options]);

    useEffect(() => {
      setSelectedPath(value);
      updateLevelOptions(value);
    }, [value, options]);

    const updateLevelOptions = (path: string[]) => {
      const newLevelOptions: CascaderOption[][] = [options];
      let currentOptions = options;

      for (let i = 0; i < path.length; i++) {
        const selectedOption = currentOptions.find(opt => opt.value === path[i]);
        if (selectedOption?.children) {
          newLevelOptions.push(selectedOption.children);
          currentOptions = selectedOption.children;
        }
      }

      setLevelOptions(newLevelOptions);
    };

    const handleOptionClick = (option: CascaderOption, level: number) => {
      const newPath = [...selectedPath.slice(0, level), option.value];
      setSelectedPath(newPath);

      if (option.children && option.children.length > 0) {
        // 有子选项，更新级联选项
        updateLevelOptions(newPath);
        setCurrentLevel(level + 1);
      } else {
        // 没有子选项，完成选择
        const selectedOptions = getSelectedOptions(newPath);
        onChange?.(newPath, selectedOptions);
        setOpen(false);
      }
    };

    const getSelectedOptions = (path: string[]): CascaderOption[] => {
      const result: CascaderOption[] = [];
      let currentOptions = options;

      for (const value of path) {
        const option = currentOptions.find(opt => opt.value === value);
        if (option) {
          result.push(option);
          currentOptions = option.children || [];
        }
      }

      return result;
    };

    const getDisplayText = () => {
      if (selectedPath.length === 0) return placeholder;
      const selectedOptions = getSelectedOptions(selectedPath);
      return selectedOptions.map(opt => opt.label).join(" / ");
    };

    return (
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Trigger asChild>
          <button
            ref={ref}
            className={cn(
              "flex h-9 w-full items-center justify-between rounded border border-border bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-secondary-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50",
              className,
            )}
            disabled={disabled}
          >
            <span className={cn("truncate", selectedPath.length === 0 && "text-secondary-foreground")}>
              {getDisplayText()}
            </span>
            <CaretUpDown className="size-4 opacity-50" />
          </button>
        </PopoverPrimitive.Trigger>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            className={cn(
              "z-50 min-w-80 overflow-hidden rounded border border-border bg-background text-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
            )}
            align="start"
            sideOffset={4}
          >
            <div className="flex max-h-80">
              {levelOptions.map((levelOpts, level) => (
                <div
                  key={level}
                  className={cn(
                    "flex-1 min-w-32 border-r border-border last:border-r-0",
                    level > 0 && "border-l border-border"
                  )}
                >
                  <div
                    className="max-h-80 overflow-y-auto p-1 scrollbar-hide"
                    style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
                    onWheel={(e) => {
                      // 防止父级 ScrollArea 抢占滚动事件（桌面）
                      e.stopPropagation();
                    }}
                    onTouchMove={(e) => {
                      // 防止父级 ScrollArea 抢占触摸滚动（移动端）
                      e.stopPropagation();
                    }}
                  >
                    {levelOpts.map((option) => {
                      const isSelected = selectedPath[level] === option.value;
                      const hasChildren = option.children && option.children.length > 0;

                      return (
                        <div
                          key={option.value}
                          className={cn(
                            "relative flex w-full cursor-pointer select-none items-center rounded py-1.5 pl-2 pr-8 text-sm outline-none hover:bg-secondary focus:bg-secondary",
                            isSelected && "bg-secondary text-secondary-foreground"
                          )}
                          onClick={() => handleOptionClick(option, level)}
                        >
                          <span className="flex-1 truncate">{option.label}</span>
                          {hasChildren && (
                            <CaretRight className="absolute right-2 size-4 opacity-50" />
                          )}
                          {isSelected && !hasChildren && (
                            <Check className="absolute right-2 size-4" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    );
  }
);

Cascader.displayName = "Cascader";
